/**
 * SectionContainer.tsx -- Width-variant section wrapper with optional
 * HTML5 landmark semantics.
 *
 * Provides three layout widths:
 *   - editorial (default): maxWidth 1080px, auto-centred
 *   - wide: maxWidth 1280px, auto-centred
 *   - full: no maxWidth constraint, left/right margin 0
 *
 * When the `landmark` prop is provided the wrapper renders the
 * corresponding HTML5 landmark element instead of a generic `<div>`:
 *   - "main"          → <main>
 *   - "navigation"    → <nav>
 *   - "complementary" → <aside>
 *   - "contentinfo"   → <footer>
 *   - "search"        → <search> (HTML5.2)
 *   - "form"          → <form>
 *
 * NOTE: PageShell already renders a single `<main id="main">` landmark
 * wrapping all route content. Screens inside PageShell should NOT pass
 * `landmark="main"` to avoid duplicate main landmarks. Use `landmark`
 * for supplementary landmarks (navigation, complementary, etc.) only.
 *
 * All variants apply var(--space-section) top margin.
 * All colour, spacing, and radius values reference CSS custom properties
 * from tokens.css -- zero hardcoded design values.
 *
 * @see specs/integration-plan.md Section 4.6
 * @see specs/prd.md FR-006, FR-017 (landmark support)
 */
import React from "react";

/**
 * Supported HTML5 landmark roles that map to semantic elements.
 */
export type LandmarkRole =
  | "main"
  | "navigation"
  | "complementary"
  | "contentinfo"
  | "search"
  | "form";

export interface SectionContainerProps {
  variant?: "editorial" | "wide" | "full";
  /**
   * When set, renders the corresponding HTML5 landmark element instead
   * of a generic `<div>`. Consumers should verify that the resulting
   * landmark does not duplicate an existing landmark on the page
   * (e.g. PageShell already provides `<main>`).
   */
  landmark?: LandmarkRole;
  /**
   * Optional aria-label forwarded to the landmark element.
   * Recommended for `navigation` and `complementary` landmarks when
   * multiple instances exist on the same page.
   */
  "aria-label"?: string;
  children: React.ReactNode;
}

const baseStyle: React.CSSProperties = {
  marginTop: "var(--space-section)",
};

const variantStyles: Record<
  NonNullable<SectionContainerProps["variant"]>,
  React.CSSProperties
> = {
  editorial: {
    maxWidth: "1080px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  wide: {
    maxWidth: "1280px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  full: {
    marginLeft: "0",
    marginRight: "0",
  },
};

/**
 * Maps a landmark role to the corresponding HTML5 element tag.
 */
const LANDMARK_TAG_MAP: Record<LandmarkRole, keyof React.JSX.IntrinsicElements> = {
  main: "main",
  navigation: "nav",
  complementary: "aside",
  contentinfo: "footer",
  search: "search",
  form: "form",
};

export function SectionContainer({
  variant = "editorial",
  landmark,
  "aria-label": ariaLabel,
  children,
}: SectionContainerProps) {
  const style: React.CSSProperties = {
    ...baseStyle,
    ...variantStyles[variant],
  };

  const Tag = landmark ? LANDMARK_TAG_MAP[landmark] : "div";

  return (
    <Tag style={style} aria-label={ariaLabel}>
      {children}
    </Tag>
  );
}

export default SectionContainer;
